import { h, Fragment } from 'preact'
import { useState, useEffect, useRef } from 'preact/hooks'
import classnames from 'classnames'


export function FileUpload({ 
	name, 
	accept, 
	initialValue, 
	error: extError, 
	fileName: extFileName, 
	onFileSelect = () => {},
	onFileDeselect = () => {},
}){

	const fileInput = useRef()
	const [dragRef, dragging, dragOver ] = useDragging()

	// basic props
	const [file, setFile] = useState(null)
	const [fileName, setFileName] = useState(extFileName)
	const [hoverError, setHoverError] = useState('')
	const [value, setValue] = useState(initialValue)
	const [error, setError] = useState(extError)
	const [progress, setProgress] = useState(false)
	const [processing, setProcessing] = useState(false)
	const [complete, setComplete] = useState(false)

	// on prop change, set internal state to match
	useEffect(() => setError(extError), [extError])
	useEffect(() => setFileName(extFileName), [extFileName])

	// on file change, call external callbacks
	useEffect(() => {
		if (!file) return
		const valid = isValidType(file.type, accept)
		if (valid) {
			setError(false)
			setFileName(file.name)
			setProgress(0.001)
			onFileSelect && onFileSelect({ file, name, setProgress, setProcessing, setComplete, setValue, setError })
		}
	}, [file])

	useEffect(() => {
		if (error || complete) {
			setProcessing(false)
			setProgress(false)
		}
	}, [error, complete])

	// handlers
	const onInputChange = event => {
		const file = event.currentTarget.files?.[0]
		if (isValidType(file.type, accept)) {
			removeFile()
			setFile(file)
		}
	}

	const onContainerDrop = event => {
		const file = event.dataTransfer.items?.[0]
		if (canDrag() && isValidType(file.type, accept)) {
			removeFile()
			setFile(event.dataTransfer.files?.[0])
			setHoverError(false)
		}
	}

	const onDragOver = event => {
		event.preventDefault()
		const file = event.dataTransfer.items?.[0]
		isValidType(file.type, accept) ? setHoverError(false) : setHoverError('Invalid file type')
	}

	const removeFile = () => {
		fileInput.value = null
		setError(false)
		setFile(false)
		setFileName(false)
		setValue('')
		setHoverError(false)
		setProgress(false)
		setProcessing(false)
		setComplete(false)
	}

	const handleRemoveFile = () => {
		onFileDeselect && onFileDeselect({ file: null, name })
		removeFile()
	}

	// helpers
	const canDrag = () => (!fileName || error)
	const isInitial = () => (!fileName && !progress && !processing && !complete)
	const isInitialSelected = () => (fileName && !progress && !processing && !complete)
	const isHoveringFile = () => (dragging && isInitial())
	const isDragOver = () => (dragging && dragOver && isInitial())
	const isUploadingFile = () => (fileName && progress && !processing && !complete)
	const isProcessingFile = () => (fileName && processing && !complete) 
	const isFinal = () => (fileName && complete)

	const classes = classnames(
		'file-upload',
		isInitial() && !dragging && 'file-upload--initial',
		isInitial() && dragging && 'file-upload--dragging',
		isHoveringFile() && 'file-upload--drag',
		dragOver && canDrag() && 'file-upload--drag-over',
		isUploadingFile() && 'file-upload--uploading',
		isProcessingFile() && 'file-upload--processing',
		dragOver && !canDrag() && 'file-upload--disable-drag-over',
		(isFinal() || isInitialSelected()) && 'file-upload--final'
	)

	return <>
		<input ref={fileInput} name={name} type="file" hidden accept={accept} onChange={onInputChange} onClick={e => e.target.value = ''} />
		<div ref={dragRef} class={classes} onDrop={onContainerDrop} onDragOver={onDragOver}>
		    <div class="file-upload__inner">
		    	{isInitial() && !dragging && <>
		    		<div className="file-upload__center first-last">
			    		<i class="icon icon--large icon--download margin-bottom-1" />
			    		<div class="file-upload__title">Drag and drop to upload</div>
			    		<div class="file-upload__or">or</div>
			    		<button class="button button--fill button--teal button--smaller file-upload__button" type="button" onClick={() => fileInput.current.click()}>Browse files</button>
			    		{error && <>
			    			<div><small class="form-error">{error}</small></div>
			    		</>}
			    	</div>
		    	</>}

		    	{isInitial() && dragging && <>
		    		<div className="file-upload__center first-last">
			    		<i class="icon icon--large icon--download margin-bottom-1" />
			    		<div class="file-upload__title">Drop file to upload</div>
			    		{hoverError && <div><small class="form-error">{hoverError}</small></div>}
		    		</div>
		    	</>}

		    	{isUploadingFile() && <>
		    		<div className="file-upload__center first-last">
			    		<div>Uploading… <br /></div>
			    		{progress ? <progress max="100" value={progress}></progress> : ''}
		    		</div>
		    	</>}

				{isProcessingFile() && <>
					<div className="file-upload__center first-last">
						<span>Processing…</span>
					</div>
				</>}

				{(isFinal() || isInitialSelected()) && <>
					<div className="file-upload__center first-last">
						<div>Uploaded: {fileName}</div>
						<button type="button" class="margin-top-1 button button--fill button--grey button--smaller file-upload__button" onClick={handleRemoveFile}>Remove file</button>
						{error && <>
							<div><small class="form-error">{error}</small></div>
						</>}
					</div>
				</>}
			</div>
		</div>
		
	</>
}


function isValidType(type, accept) {
	if (typeof accept !== 'string') return false
	const types = accept.split(',').map(s => s.trim()).filter(Boolean)
	return types.indexOf(type) > -1
}


function useDragging() {
	const dragRef = useRef()
	const [dragging, setDragging] = useState(false)
	const [dragOver, setDragOver] = useState(false)

	const onEnter = e => {
		setDragging(true)
		if (!dragRef.current) return
		if (dragRef.current.contains(e.fromElement)) setDragOver(false)
	}
	
	const onLeave = e => {
		setDragging(true)
		if (!e.fromElement) setDragging(false)
	}

	const onAllDrop = e => {
		event.preventDefault()
		setDragging(false)
		setDragOver(false)
	}
	
	// prevent onDragOver from loading file in new tab by default
	const onDragOver = e => e.preventDefault()

	const onDragEnter = e => {
		e.preventDefault()
		e.stopPropagation()
		setDragOver(true)
	}
	
	useEffect(() => {
		dragRef.current.addEventListener('dragenter', onDragEnter)
		document.addEventListener('dragenter', onEnter)
		document.addEventListener('dragleave', onLeave)
		document.addEventListener('drop', onAllDrop)
		document.addEventListener('dragover', onDragOver)
		return () => {
			dragRef.current.removeEventListener('dragenter', onDragEnter)
			document.removeEventListener('dragenter', onEnter)
			document.removeEventListener('dragleave', onLeave)
			document.removeEventListener('drop', onAllDrop)
			document.removeEventListener('dragover', onDragOver)
		}
	}, [dragRef])

	return [dragRef, dragging, dragOver]
}